import {
  Gradient,
  NumberConfig,
  PlayersAuthenticationData,
  Prices,
  EFontWeights,
  BadgeData,
  ProductData,
  EBundlesViewModel,
  EBalancesTheme,
  BundleProps,
} from '@appcharge/shared-ui';
import {
  EBundlesInternalViewModel,
  ELogoSize,
  EPlatformType,
  ESectionsViewModel,
  ESpecialOfferInternalViewModel,
  ESupportModel,
  EFeatureFlag,
  ESaleDiscountDisplayType,
  EPopupSubType,
  StoreBodyBlock,
  EOfferType,
  ESubRollingOfferCollectBehavior,
  EOrderStatus,
  ERollingOffersInternalViewModel,
  EButtonVariant,
  EPosition,
} from './enums';

export interface APIProps {
  orderId?: string;
  bundleId?: string;
  platform?: string;
  isSSE?: boolean;
}

export interface LoginResponse {
  profileImageUrl: string;
  playerProfileImage?: string;
  sessionToken: string;
  errorCode: number;
  errorMessage: string;
  playerId: string;
  playerName: string;
  playerCountry: string;
  offerId?: string;
}

interface DeepLink {
  platform: EPlatformType;
  deepLink: string;
}

interface SupportConfiguration {
  internalSupportUrl: string;
  supportModel: ESupportModel;
  preLoginSnippet: string;
  postLoginSnippet: string;
  supportLinkParameters?: {
    playerIdParamName?: string;
    playerNameParamName?: string;
  };
}

interface IntegrationResponse {
  backToGameButtonOn: boolean;
  backToStoreButtonOn: boolean;
  deepLinks: DeepLink[];
  googleAnalyticsMeasurementId: string;
  logRocketAppId: string;
  logRocketPublisherId: string;
  playersAuthentication: PlayersAuthenticationData;
  shouldUseLogRocket: boolean;
  eventsWebhookUrl: string;
  eventsV2WebhookUrl: string;
  enabledWebhookEvents: string[];
  enabledWebhookEventsV2: string[];
  checkoutPublicKey: string;
  isProfilesEnabled?: boolean;
}

export interface GeneralTheme {
  productsQuantityFormat?: NumberConfig;
  backgroundImageDesktop: string;
  backgroundImageMobile: string;
  storeTabName: string;
  bouncyScrollOn?: boolean;
  storeSaleImage?: string;
  bundlesInternalViewModel: EBundlesInternalViewModel;
  bundlesViewModel: EBundlesViewModel;
  rollingOffersInternalViewModel: ERollingOffersInternalViewModel;
  sectionViewModel?: ESectionsViewModel;
  loaderAnimation?: string;
  showRotatePrompt?: boolean;
  favicon: string;
  font: string;
  logo: string;
  logoSize?: ELogoSize;
  footer: FooterSettings;
  specialOffersViewModel: string;
  specialOffersInternalViewModel: ESpecialOfferInternalViewModel;
  specialOfferQuantityFormat: NumberConfig;
  balancesTheme?: EBalancesTheme;
  popupRatio?: string;
  maintenanceModeRichText?: MaintenanceRichText;
}

export interface LoginTheme {
  errorMessage?: {
    title: string;
    subTitle: string;
  };
  text: string;
  textColor: string;
  textSize: number;
  textWeight: EFontWeights;
  font?: string;
  otpButton: {
    backgroundColor: Gradient;
    textColor: Gradient;
    text: string;
    icon: string;
  };
  facebookButton?: {
    variant: EButtonVariant;
  };
  consentSection: {
    htmlText: string;
    consentRequired: boolean;
  };
  loginBackgroundImageMobile?: string;
  loginBackgroundImageDesktop?: string;
  loginLogoImage?: string;
  loginLogoSize: ELogoSize;
  accountApprovalModal?: AccountApprovalModalDesign;
}

export interface AccountApprovalModalDesign {
  backgroundImage: string;
  backgroundColor: Gradient;
  borderColor: string;
  borderWidth: number;
  buttonBackgroundColor: Gradient;
  buttonTextColor: string;
  textColor: string;
  additionalText?: string;
}

export interface SeoSettings {
  title?: string;
  description?: string;
  keywords?: string[];
  socialShareImageUrl?: string;
  socialTitle?: string;
  socialDescription?: string;
}

export interface FooterSettings {
  imageMobile?: string;
  imageDesktop?: string;
  appstoreLink?: string;
  googlePlayLink?: string;
  privacyPolicyUrl: string;
  termsAndConditionsUrl: string;
  supportLink?: string;
  openLinksInNewTab: boolean;
  showAppchargeLogo?: boolean;
  showCookieSettings?: boolean;
  backgroundOpacity?: number;
  textColor?: Gradient;
  supportParams?: {
    playerIdParamName?: string;
    playerNameParamName?: string;
  };
  enableRefundFooterLink?: boolean;
  enabledRefundFooterLinkCountries?: string[];
}

export interface MaintenanceRichText {
  fontColor?: string;
  text: string;
}

export interface StoreSettings {
  logoutFont?: string;
  storeTabName?: string;
  themeColor?: string;
  bouncyScrollOn?: boolean;
  favicon?: string;
  loaderAnimation?: string;
  defaultPlayerImage?: string;
  footer?: Partial<FooterSettings>;
  emptyState?: {
    noOffersTitleText?: string;
    noOffersMessageText?: string;
  };
  seo?: SeoSettings;
  maintenanceModeRichText?: MaintenanceRichText;
}

export interface ResolvedStoreSettings {
  storeTabName: string;
  themeColor: string;
  bouncyScrollOn?: boolean;
  favicon: string;
  loaderAnimation?: string;
  defaultPlayerImage?: string;
  footer: FooterSettings;
  noOffersTitleText?: string;
  noOffersMessageText?: string;
  seo?: SeoSettings;
  maintenanceModeRichText?: MaintenanceRichText;
}

export interface StoreTheme {
  bundleBorderColor: Gradient;
  borderRadius?: number;
  noOffersMessageText?: string;
  noOffersTitleText?: string;
  publisherFooterImage?: string;
  buttonColor: Gradient;
  buttonTextColor: string;
  addToHomeScreen?: {
    borderColor: Gradient;
    backgroundColor: Gradient;
    active: boolean;
    buttonImage: string;
    iconImage: string;
    shortcutName: string;
    verticalOffset: number;
  };
  backToGameButton?: {
    active: boolean;
    buttonImage: string;
    position?: EPosition;
  };
  defaultBanner?: string;
  defaultPlayerImage?: string;
  header?: {
    backgroundOpacity?: number;
    showLogo?: boolean;
    showPlayerName?: boolean;
    showPlayerId?: boolean;
    isSticky?: boolean;
    textColor?: Gradient;
  };
  seo?: SeoSettings;
}

export interface PaymentModalTheme {
  headerColor: Gradient;
  headerSize: number;
  headerText: string;
  popupBorderColor: Gradient;
  popupColor: Gradient;
  text: string;
  textColor: string;
  textSize: number;
  textWeight: EFontWeights;
}

export interface StoreTemplateTheme {
  bodyBlocksOrder: StoreBodyBlock[];
}

export interface CompletedScreenTheme {
  backToGameButtonText: string;
  headerColor: Gradient;
  headerSize: number;
  headerText: string;
  headerWeight: EFontWeights;
  text: string;
  textColor: string;
  textSize: number;
  textWeight: EFontWeights;
}

export interface StoreThemeResponse {
  completedScreen: CompletedScreenTheme;
  general: GeneralTheme;
  login: LoginTheme;
  paymentScreen: PaymentModalTheme;
  publisherId: string;
  storeScreen: StoreTheme;
  storeTemplate: StoreTemplateTheme;
  storeSettings?: StoreSettings;
}

interface ExternalService {
  gaActive: boolean;
  logRocketActive: boolean;
  mixpanelActive: boolean;
}

export interface Profile {
  name: string;
  profileId: string;
  fbAppId?: string;
  otpDeepLink?: {
    otpGenerateDeeplinkUrl: string;
    otpTextModel: string;
    otpOn: boolean;
  };
}

export interface BootResponse {
  externalServicesConfig: ExternalService;
  integration: IntegrationResponse;
  currencyCode: string;
  language: string;
  storeTheme: StoreThemeResponse;
  supportConfiguration: SupportConfiguration;
  featureFlags: Record<EFeatureFlag, boolean>;
}

export interface BrandKit {
  customArrowImage?: string;
}

export enum EBlockType {
  BANNER = 'Banner',
  BUNDLE = 'Bundle',
  PROMOTION = 'Promotion',
  ROLLING_OFFER = 'RollingOffer',
  ONE_PLUS_MANY = 'OnePlusMany',
  DAILY_BONUS = 'DailyBonus',
  REWARD_CALENDAR = 'RewardCalendar',
  TRIGGERED_POPUP = 'TriggeredPopup',
  PROGRESS_BAR = 'ProgressBar',
  RICH_TEXT = 'RichText',
}

export interface BodyBlockV2Item {
  blocks: string[];
  isVisible: boolean;
  isMergeable: boolean;
}

export interface StoreBlockMaxOffers {
  unlimited: boolean;
  value?: number;
}

export interface IBaseStoreBlock {
  id: string;
  type: EBlockType;
  isVisible: boolean;
  mergedTo?: string[];
  selectedDesignId?: string;
  themes?: string[];
}

export interface IBannerStoreBlock extends IBaseStoreBlock {
  type: EBlockType.BANNER;
}

export interface IBundleStoreBlock extends IBaseStoreBlock {
  type: EBlockType.BUNDLE;
  offersPerRow?: number;
  bundleViewModel?: string;
}

export interface IPromotionStoreBlock extends IBaseStoreBlock {
  type: EBlockType.PROMOTION;
}

export interface IProgressBarStoreBlock extends IBaseStoreBlock {
  type: EBlockType.PROGRESS_BAR;
}

export interface IRollingOfferStoreBlock extends IBaseStoreBlock {
  type: EBlockType.ROLLING_OFFER;
  desktopLayout?: string;
}

export interface IOnePlusManyStoreBlock extends IBaseStoreBlock {
  type: EBlockType.ONE_PLUS_MANY;
  desktopLayout?: string;
  maxOffers?: StoreBlockMaxOffers;
  rewardsLayout?: string;
}

export interface IDailyBonusStoreBlock extends IBaseStoreBlock {
  type: EBlockType.DAILY_BONUS;
  selectedBadgeDesignId?: string;
  badgeThemes?: string[];
}

export interface IRewardCalendarStoreBlock extends IBaseStoreBlock {
  type: EBlockType.REWARD_CALENDAR;
  selectedBadgeDesignId?: string;
}

export interface ITriggeredPopupStoreBlock extends IBaseStoreBlock {
  type: EBlockType.TRIGGERED_POPUP;
  selectedBadgeDesignId?: string;
}

export interface IRichTextStoreBlock extends IBaseStoreBlock {
  type: EBlockType.RICH_TEXT;
  text?: string;
  fontColor?: string;
}

export type TStoreBlock =
  | IBannerStoreBlock
  | IBundleStoreBlock
  | IPromotionStoreBlock
  | IProgressBarStoreBlock
  | IRollingOfferStoreBlock
  | IOnePlusManyStoreBlock
  | IDailyBonusStoreBlock
  | IRewardCalendarStoreBlock
  | ITriggeredPopupStoreBlock
  | IRichTextStoreBlock;

export interface TemplateResponse {
  brandKit: BrandKit;
  storeBlocks?: TStoreBlock[];
  bodyBlocksOrderV2?: BodyBlockV2Item[];
}

export interface OTPResponse {
  deepLink: string;
  accessToken: string;
  desktopAutoRedirect?: boolean;
}

interface IOfferBadges {
  emblems?: BundleProps['emblemBadges'];
  ribbons?: BundleProps['ribbonBadges'];
}

export interface OfferUI {
  backgroundImage: string;
  backgroundColor?: Gradient;
  specialOffer?: {
    backgroundColor: Gradient;
    title: string;
    fontWeight: EFontWeights;
    fontSize: number;
    fontColor: Gradient;
    subTitle: Title;
    presentOfferEndTimer: boolean;
  };
  rollingOffer?: {
    arrowColor: string;
    backgroundColor: Gradient;
    collectBehavior: ESubRollingOfferCollectBehavior;
    subRollingOffer: {
      backgroundColor?: Gradient;
      backgroundImage?: string;
      lockImage: string;
      collectText?: string;
    };
  };
  onePlusMany?: {
    headerImage?: string;
    plusImage?: string;
    freeTilesLayout?: '1-in-a-row' | '2-in-a-row' | '3-in-a-row';
    collectText?: string;
    backgroundOpacity?: number;
    subOffer: {
      backgroundColor?: Gradient;
      lockImage?: string;
      backgroundImage?: string;
      freeTilesBackgroundImage?: string;
      claimedImage?: string;
    };
  };
  borderColor?: Gradient;
  borderWidth?: number;
  badgeCoverImage?: string;
}

export interface SequencedProduct {
  index: number;
  prices: Prices;
  products: ProductData[];
  barSize?: number;
}

export interface TimeFrameObject {
  startTime: Date;
  endTime: Date;
  id?: string;
  notes?: string;
}

export interface PriceDiscount {
  priceBeforeDiscount?: number | null;
  type?: ESaleDiscountDisplayType;
  discount?: number | null;
}

export interface ProductSale {
  amountBeforeSale?: number | null;
  type?: ESaleDiscountDisplayType;
  sale?: number | null;
}

export interface OfferData {
  offerPlayerRemainingAvailability?: number;
  playerAvailability?: {
    total: number;
    remaining: number;
    hidePlayerAvailability?: boolean;
  };
  purchasedAmount?: number;
  offerId: string;
  offerJwt?: string;
  offerName: string;
  offerType: EOfferType;
  offerSectionId?: string;
  offerTabId?: string;
  productsSequence: SequencedProduct[];
  offerUi: OfferUI;
  offerBadges?: IOfferBadges;
  priceDiscount?: PriceDiscount;
  productSale?: ProductSale;
  badges?: BadgeData[]; // TODO remove old badge schema
  dynamicOfferUi?: {
    salePercentage?: number;
  };
  showEvery?: number;
  subType?: EPopupSubType;
  publisherOfferId?: string;
  isAllowedToCollect?: boolean;
  priority?: number;
  offerIndex?: number;
  indexToCollect?: number;
  startOver?: boolean;
  isNewForPlayer?: boolean;
  isBadgeNextProduct?: boolean;
  schedule?: {
    permanent: boolean;
    timeFrames: TimeFrameObject[];
  };
  activeMissionIndex?: number;
  collectedPoints?: number;
  lastStartedMissionIndex?: number;
  displayRule?: {
    frequency: number;
    cooldownInMinutes: number;
    lastInteractionTime?: string;
  };
  rewardCalendar?: {
    unlockedIndex: number;
    nextUnlockAt: Date;
  };
  lockedStateButtonText?: string;
}

export interface OrderProduct {
  quantity: number;
  quantityDisplay: string;
  image: string;
  textFontColorHex: string;
  prefix?: string;
  suffix?: string;
}

export interface OrderData {
  offerId: string;
  offerName: string;
  price: number;
  products: OrderProduct[];
  offerType: EOfferType;
  subType?: EPopupSubType;
  salePercentage?: number;
  priceInUsd?: number;
  indexToCollect?: number;
}

export interface OrderResponse {
  offer: OrderData;
  state: EOrderStatus;
  publisherErrorMessage?: string;
  reason?: string;
}

export interface SectionData {
  name: string;
  sectionId: string;
  image?: string;
}

export interface ResponseTabData {
  name: string;
  publisherId: string;
  publisherTabId: string;
  tabId: string;
  iconUrl?: string;
}

export interface TabData {
  name: string;
  id: string;
  iconUrl?: string;
}

export interface FacebookPictureResponse {
  picture?: {
    data?: {
      url?: string;
    };
  };
}

export interface GoogleProfileResponse {
  picture: string;
}

export interface Title {
  text: string;
  fontSize: number;
  fontWeight: string;
  fontColor: Gradient;
}

export interface JWTObject {
  d10xPayload: string;
  iat: number;
  originalLink: string;
  playerId: string;
}
